import { InMemoryCache, makeVar } from '@apollo/client';

import { authErrorMessageVar } from './common/authErrorMessageReactiveVar';
import { majorIdVar } from './common/majorIdReactiveVar';
import { platformValidationCodeReactiveVar } from './common/platformValidationCodeReactiveVar';
import { renderNotification } from './learner/homeReactiveVars';
import { jobPreferencesVar } from './learner/jobPreferencesReactiveVar';
import { jobTitlesInstitutionReactiveVar } from './learner/jobTitlesInstitutionReactiveVar';
import { kanbanDataVar } from './learner/kanbanReactiveVar';
import { learningPathsByInstitutionVar } from './learner/learningPathsByInstitutionReactiveVar';
import { learningPathsByObjectivesVar } from './learner/learningPathsByObjectivesReactiveVar';
import { learningPathsDrawerVar } from './learner/learningPathsDrawerReactiveVar';
import { RecommendedOccupationsByMajorReactiveVar } from './learner/recommendedOccupationsByMajorReactiveVar';
import { RecommendedOccupationsReactiveVar } from './learner/recommendedOccupationsReactiveVar';
import { recommendedSkillsVar } from './learner/recommendedSkillsReactiveVar';
import { RelatedOccupationsReactiveVar } from './learner/relatedOccupationsReactiveVar';
import { selectedOptionsSkillVar } from './learner/selectedOptionsSkillVar';
import { SkillForCoursesRecommendationReactiveVar } from './learner/skillForCoursesRecommendationReactiveVar';
import { skillMatchVar } from './learner/skillMatchReactiveVar';
import { studentLearningPathsVar } from './learner/studentLearningPathsReactiveVar';
import {
  isValidMajorForm,
  majorListWizard,
  newFormsMajor,
  newStudentMajorForms,
  successSaveNewMajors,
  userIdActive,
} from './wizard/wizardReactiveVars';
import { INotification } from '../contexts/reducer/notificationReducer';
import StudentSkilltrackerFiltersEnum from '../views/Learner/Skills/components/KanbanBoard/interfaces/StudentSkilltrackerFiltersEnum';
import StudentSkilltrackerProficiencyLevelFiltersEnum from '../views/Learner/Skills/components/KanbanBoard/interfaces/StudentSkilltrackerProficiencyLevelFiltersEnum';

export const occupationJobName = makeVar<string>('');
export const skillIdVar = makeVar<string | undefined>(undefined);
export const skillTypeVar = makeVar<string | undefined>(undefined);
export const skillTranslatedTypeVar = makeVar<string>('');
export const skillNameVar = makeVar<string>('');
export const originalSkillNameVar = makeVar<string>('');
export const occupationPreferenceId = makeVar<string | undefined>(undefined);
export const studentId = makeVar<number | undefined>(undefined);
export const userMajors = makeVar<undefined | { name: string }[]>(undefined);
export const kanbanColumnIdVar = makeVar<string | undefined>(undefined);
export const kanbanSkillType = makeVar<string>(StudentSkilltrackerFiltersEnum.ALL);
export const kanbanJobTitleId = makeVar<string>(StudentSkilltrackerFiltersEnum.ALL);
export const kanbanProficiencyLevel = makeVar<StudentSkilltrackerProficiencyLevelFiltersEnum>(
  StudentSkilltrackerProficiencyLevelFiltersEnum.ALL,
);
export const loader = makeVar<string[]>([]);
export const notificationsVar = makeVar<INotification[]>([]);
export const userRoleVar = makeVar<string>('');
export const occupationFilterIdExplore = makeVar<string | undefined>(undefined);
export const disableSkillSetChange = makeVar<boolean>(false);
export const loadingSSO = makeVar<boolean>(false);
export const loadingWizard = makeVar<boolean>(false);
export const wizardSkillStepSkils = makeVar<string[] | undefined>(undefined);
export const wizardSkillToLearnStepSkills = makeVar<string[] | undefined>(undefined);
export const eventTime = makeVar<number>(Date.now());
export const pathnameEvent = makeVar<string | undefined>(undefined);
export { learningPathsDrawerVar };
export const collectionsOffsetVar = makeVar<number>(0);
export const jobTitleIdForJobPostings = makeVar<string | undefined>(undefined);

export const cache: InMemoryCache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        majorId: {
          read() {
            return majorIdVar();
          },
        },
        skillId: {
          read() {
            return skillIdVar();
          },
        },
        skillType: {
          read() {
            return skillTypeVar();
          },
        },
        skillTranslatedType: {
          read() {
            return skillTranslatedTypeVar();
          },
        },
        occupationJobName: {
          read() {
            return occupationJobName();
          },
        },
        occupationPreferenceId: {
          read() {
            return occupationPreferenceId();
          },
        },
        userMajors: {
          read() {
            return userMajors();
          },
        },
        skillName: {
          read() {
            return skillNameVar();
          },
        },
        originalSkillName: {
          read() {
            return originalSkillNameVar();
          },
        },
        kanbanColumnId: {
          read() {
            return kanbanColumnIdVar();
          },
        },
        kanbanSkillType: {
          read() {
            return kanbanSkillType();
          },
        },
        kanbanJobTitleId: {
          read() {
            return kanbanJobTitleId();
          },
        },
        kanbanProficiencyLevel: {
          read() {
            return kanbanProficiencyLevel();
          },
        },
        majorListWizard: {
          read() {
            return majorListWizard();
          },
        },
        isValidMajorForm: {
          read() {
            return isValidMajorForm();
          },
        },
        newStudentMajorForms: {
          read() {
            return newStudentMajorForms();
          },
        },
        userIdActive: {
          read() {
            return userIdActive();
          },
        },
        successSaveNewMajors: {
          read() {
            return successSaveNewMajors();
          },
        },
        newFormsMajor: {
          read() {
            return newFormsMajor();
          },
        },
        renderNotification: {
          read() {
            return renderNotification();
          },
        },
        SkillForCoursesRecommendationReactiveVar: {
          read() {
            return SkillForCoursesRecommendationReactiveVar();
          },
        },
        RelatedOccupationsReactiveVar: {
          read() {
            return RelatedOccupationsReactiveVar();
          },
        },
        RecommendedOccupationsReactiveVar: {
          read() {
            RecommendedOccupationsReactiveVar();
          },
        },
        loader: {
          read() {
            return loader();
          },
        },
        RecommendedOccupationsByMajorReactiveVar: {
          read() {
            return RecommendedOccupationsByMajorReactiveVar();
          },
        },
        notificationsVar: {
          read() {
            return notificationsVar();
          },
        },
        jobPreferencesVar: {
          read() {
            return jobPreferencesVar();
          },
        },
        kanbanDataVar: {
          read() {
            return kanbanDataVar();
          },
        },
        recommendedSkillsVar: {
          read() {
            return recommendedSkillsVar();
          },
        },
        skillMatchVar: {
          read() {
            return skillMatchVar();
          },
        },
        userRoleVar: {
          read() {
            return userRoleVar();
          },
        },
        occupationFilterIdExplore: {
          read() {
            return occupationFilterIdExplore();
          },
        },
        disableSkillSetChange: {
          read() {
            return disableSkillSetChange();
          },
        },
        loadingSSO: {
          read() {
            return loadingSSO();
          },
        },
        collectionsOffsetVar: {
          read() {
            return collectionsOffsetVar();
          },
        },
        platformValidationCodeVar: {
          read() {
            return platformValidationCodeReactiveVar();
          },
        },
        loadingWizard: {
          read() {
            return loadingWizard();
          },
        },
        wizardSkillStepSkils: {
          read() {
            return wizardSkillStepSkils();
          },
        },
        wizardSkillToLearnStepSkills: {
          read() {
            return wizardSkillToLearnStepSkills();
          },
        },
        eventTime: {
          read() {
            return eventTime();
          },
        },
        pathnameEvent: {
          read() {
            return pathnameEvent();
          },
        },
        selectedOptionsSkillVar: {
          read() {
            return selectedOptionsSkillVar();
          },
        },
        jobTitlesInstitutionReactiveVar: {
          read() {
            return jobTitlesInstitutionReactiveVar();
          },
        },
        authErrorMessageVar: {
          read() {
            return authErrorMessageVar();
          },
        },
        studentLearningPathsVar: {
          read() {
            return studentLearningPathsVar();
          },
        },
        learningPathsByInstitutionVar: {
          read() {
            return learningPathsByInstitutionVar();
          },
        },
        majorsByInstitution: {
          keyArgs: ['institutionId'],
          merge(existing = [], incoming: any[]) {
            const existingMap = new Map(existing.map((major: any) => [major.id, major]));

            incoming.forEach((newMajor: any) => {
              if (newMajor && typeof newMajor === 'object') {
                const existingMajor = existingMap.get(newMajor.id) || {};

                existingMap.set(newMajor.id, { ...existingMajor, ...newMajor });
              }
            });

            return Array.from(existingMap.values()).sort((firstMajor: any, secondMajor: any) => {
              if (firstMajor.name && secondMajor.name) {
                return firstMajor.name.localeCompare(secondMajor.name);
              }

              return 0;
            });
          },
        },
        getJobTitlesPaginated: {
          keyArgs: ['search'],
          merge(existing = [], incoming) {
            return [...existing, ...incoming];
          },
        },
        getCourseCollections: {
          keyArgs: ['includeDeleted', 'skillId'],
          merge(existing = [], incoming, { args, readField }) {
            if (!incoming) {
              return existing;
            }

            const dedupeById = (list: any[]): any[] => {
              const map = new Map<string, any>();

              list.forEach((item: any) => {
                const id = (readField?.('id', item) as string | undefined) ?? (item?.id as string | undefined);

                if (id) {
                  map.set(id, item);
                }
              });

              return Array.from(map.values());
            };

            if (args?.offset === undefined || args?.offset === null || args?.offset === 0 || !existing) {
              return {
                ...incoming,
                courseCollections: dedupeById(incoming?.courseCollections ?? []),
              };
            }

            const existingCollections = existing?.courseCollections ?? [];
            const incomingCollections = incoming?.courseCollections ?? [];
            const merged = [...existingCollections, ...incomingCollections];

            return {
              ...incoming,
              courseCollections: dedupeById(merged),
            };
          },
        },
        getCoursesSaved: {
          keyArgs: ['collectionId', 'skillId', 'limit'],
          merge(existing = [], incoming, { args, readField }) {
            if (!incoming) {
              return existing;
            }

            if (args?.offset === undefined || args?.offset === null || args?.offset === 0 || !existing) {
              return incoming;
            }

            const existingCourses = existing?.coursesSaved ?? [];
            const incomingCourses = incoming?.coursesSaved ?? [];
            const merged = [...existingCourses, ...incomingCourses];
            const mergedMap = new Map<string, any>();

            merged.forEach((item: any) => {
              const id =
                (readField?.('id', item) as string | undefined) ??
                (readField?.('courseId', item) as string | undefined) ??
                (item?.courseId as string | undefined);

              if (id) {
                mergedMap.set(id, item);
              }
            });

            return {
              ...incoming,
              coursesSaved: Array.from(mergedMap.values()),
            };
          },
        },
        countries: {
          keyArgs: ['language', 'search'],
          merge(existing = [], incoming) {
            return [...existing, ...incoming];
          },
        },
        getRelatedLearningPaths: {
          keyArgs: ['occupations', 'skills', 'majors'],
          merge(existing = [], incoming, { args, readField }) {
            if (!incoming) {
              return existing;
            }

            const dedupeByPathwayId = (list: any[]): any[] => {
              const map = new Map<string, any>();

              list.forEach((item: any) => {
                const pathwayId =
                  (readField?.('pathwayId', item) as string | undefined) ?? (item?.pathwayId as string | undefined);

                if (pathwayId) {
                  map.set(pathwayId, item);
                }
              });

              return Array.from(map.values());
            };

            if (args?.page === undefined || args?.page === null || args?.page === 1 || !existing) {
              return {
                ...incoming,
                data: dedupeByPathwayId(incoming?.data ?? []),
              };
            }

            const existingLearningPaths = existing?.data ?? [];
            const incomingLearningPaths = incoming?.data ?? [];
            const merged = [...existingLearningPaths, ...incomingLearningPaths];

            return {
              ...incoming,
              data: dedupeByPathwayId(merged),
            };
          },
        },
        learningPathsByObjectivesVar: {
          read() {
            return learningPathsByObjectivesVar();
          },
        },
        learningPathsDrawerVar: {
          read() {
            return learningPathsDrawerVar();
          },
        },
      },
    },
  },
  addTypename: false,
});
